import api from './api';

// All calls route through the shared `api` axios instance (baseURL = REACT_APP_API_URL
// + '/admin'), so the 401-refresh interceptor applies. GET is staff-readable; all
// mutations are owner-only (enforced server-side).

export interface UrgencyLevel {
  id: number;
  shop_id: number;
  label: string;
  color: string;
  sort_order: number;
  is_active: boolean;
}

export interface PhotoCaption {
  id: number;
  shop_id: number;
  text: string;
  sort_order: number;
  is_active: boolean;
}

export interface CreateUrgencyLevelInput {
  label: string;
  color: string;
  sort_order?: number;
}

export interface UpdateUrgencyLevelInput {
  label?: string;
  color?: string;
  sort_order?: number;
  is_active?: boolean;
}

export interface CreatePhotoCaptionInput {
  text: string;
  sort_order?: number;
}

export interface UpdatePhotoCaptionInput {
  text?: string;
  sort_order?: number;
  is_active?: boolean;
}

// ── Urgency Levels ──────────────────────────────────────────────────────────

export const getUrgencyLevels = async (): Promise<UrgencyLevel[]> => {
  const response = await api.get('/urgency-levels');
  return response.data;
};

export const createUrgencyLevel = async (
  input: CreateUrgencyLevelInput,
): Promise<UrgencyLevel> => {
  const response = await api.post('/urgency-levels', input);
  return response.data;
};

export const updateUrgencyLevel = async (
  id: number,
  input: UpdateUrgencyLevelInput,
): Promise<UrgencyLevel> => {
  const response = await api.put(`/urgency-levels/${id}`, input);
  return response.data;
};

export const deleteUrgencyLevel = async (id: number): Promise<void> => {
  await api.delete(`/urgency-levels/${id}`);
};

/** Reorder by passing the full id list in the desired order. Returns the new list. */
export const reorderUrgencyLevels = async (ids: number[]): Promise<UrgencyLevel[]> => {
  const response = await api.put('/urgency-levels/reorder', { ids });
  return response.data;
};

// ── Photo Captions ──────────────────────────────────────────────────────────

export const getPhotoCaptions = async (): Promise<PhotoCaption[]> => {
  const response = await api.get('/photo-captions');
  return response.data;
};

export const createPhotoCaption = async (
  input: CreatePhotoCaptionInput,
): Promise<PhotoCaption> => {
  const response = await api.post('/photo-captions', input);
  return response.data;
};

export const updatePhotoCaption = async (
  id: number,
  input: UpdatePhotoCaptionInput,
): Promise<PhotoCaption> => {
  const response = await api.put(`/photo-captions/${id}`, input);
  return response.data;
};

export const deletePhotoCaption = async (id: number): Promise<void> => {
  await api.delete(`/photo-captions/${id}`);
};

/** Reorder by passing the full id list in the desired order. Returns the new list. */
export const reorderPhotoCaptions = async (ids: number[]): Promise<PhotoCaption[]> => {
  const response = await api.put('/photo-captions/reorder', { ids });
  return response.data;
};
